import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageWithSupportTicketId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  useChannelThreadsData,
  useSingleSupportTicketDataCore,
  useSupportTicketData,
  useSupportTicketMessagesData,
  useSupportTicketPresenceData,
  useSupportTicketsData,
  useSupportTicketTimelineEventsData,
  useThreadMessagesData,
} from "@redotech/redo-support-v2/query/support-ticket-data-hooks";
import type { SupportTicketThreadCollectionItem } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import type { SupportTicketPresenceRecord } from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import {
  DEFAULT_PAGE_SIZE,
  fetchSingleSupportTicket,
} from "../db/support-ticket-list-collection";
import { useQueryOptions } from "../filters/filters-context";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

export function useSupportTickets(
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
): {
  data: SupportTicketListItemResponse[];
  totalCount: number;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const queryOptions = useQueryOptions();

  return useSupportTicketsData({
    page,
    collections,
    filterGroup: queryOptions.filterGroup,
    sorts: queryOptions.sorts,
    pageSize,
  });
}

export function useSupportTicket(supportTicketId: SupportTicketId): {
  data: SupportTicketListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useSupportTicketData({
    supportTicketId,
    collections,
    fetchSupportTicket: (id) =>
      fetchSingleSupportTicket(effectApi, id, reactQueryClient),
  });
}

export function useChannelThreads(supportTicketId: SupportTicketId): {
  data: SupportTicketThreadCollectionItem[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  return useChannelThreadsData({ supportTicketId, collections });
}

export function useSupportTicketMessages(supportTicketId: SupportTicketId): {
  data: MessageWithSupportTicketId[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketMessagesData({ supportTicketId, collections });
}

export function useThreadMessages(
  supportTicketId: SupportTicketId,
  threadIds: ChannelThreadId | readonly ChannelThreadId[],
): { data: Message[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();
  return useThreadMessagesData({ supportTicketId, threadIds, collections });
}

export function useSupportTicketTimelineEvents(
  supportTicketId: SupportTicketId,
): { data: SupportTicketTimelineEventResponse[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketTimelineEventsData({ supportTicketId, collections });
}

export function useSupportTicketPresence(supportTicketId: SupportTicketId): {
  data: SupportTicketPresenceRecord[];
} {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketPresenceData({ supportTicketId, collections });
}

export function useSingleSupportTicketData(supportTicketId: SupportTicketId): {
  supportTicket: SupportTicketListItemResponse | undefined;
  threads: SupportTicketThreadCollectionItem[];
  messages: Message[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useSingleSupportTicketDataCore({
    supportTicketId,
    collections,
    fetchSupportTicket: (id) =>
      fetchSingleSupportTicket(effectApi, id, reactQueryClient),
  });
}
